import {ElementRef, Injectable} from '@angular/core';
import {WebSocketSubject} from "rxjs/internal-compatibility";
import {Message} from "./Message";
import {Vehicule} from "../../model/data-managemnt";
import {webSocket} from "rxjs/webSocket";
import {DataManagementService} from "../data-management/data-management.service";
import {BehaviorSubject, Subject, Subscription} from "rxjs";
import {stunServerUrl, turnPassword, turnServerUrl, turnUsername, wsocket} from "../../global.config";
import {tap} from "rxjs/operators/tap";
import {TranslateService} from "@ngx-translate/core";
import {ToastController} from "@ionic/angular";
import {Path} from "../../model/historical";

/** Camera recording segment from device `playback-map` response. */
export interface PlaybackMapRange {
  from: string;
  to: string;
}

export interface PlaybackMapResponsePayload {
  type: string;
  status: number;
  start: string;
  end: string;
  source: string;
  target: string;
  ranges: PlaybackMapRange[];
}

/** Camera stores recordings in GMT; format instant as `YYYYMMDD-HHmmss` for WebSocket playback-map. */
export function formatDateToPlaybackMapToken(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const h = String(d.getUTCHours()).padStart(2, '0');
  const min = String(d.getUTCMinutes()).padStart(2, '0');
  const s = String(d.getUTCSeconds()).padStart(2, '0');
  return `${y}${m}${day}-${h}${min}${s}`;
}

/** Camera stores historical video as 1-minute chunks from each map segment start. */
export const PLAYBACK_CHUNK_MS = 60_000;

/**
 * Floor a wall time to the start of the 1-minute chunk inside a playback-map segment.
 * Chunks are anchored at `segmentStartMs`, not at wall-clock minute boundaries.
 */
export function alignSeekToPlaybackChunkStart(
  seekMs: number,
  segmentStartMs: number,
  segmentEndMs?: number
): number {
  if (!Number.isFinite(seekMs) || !Number.isFinite(segmentStartMs)) {
    return seekMs;
  }
  const offset = Math.max(0, seekMs - segmentStartMs);
  const chunkIndex = Math.floor(offset / PLAYBACK_CHUNK_MS);
  let aligned = segmentStartMs + chunkIndex * PLAYBACK_CHUNK_MS;
  if (segmentEndMs != null && Number.isFinite(segmentEndMs) && segmentEndMs > segmentStartMs) {
    const lastChunkStart = segmentStartMs +
      Math.floor((segmentEndMs - segmentStartMs - 1) / PLAYBACK_CHUNK_MS) * PLAYBACK_CHUNK_MS;
    aligned = Math.min(aligned, Math.max(segmentStartMs, lastChunkStart));
  }
  return aligned;
}

/** Recording segment from playback-map that contains `seekMs` (half-open [from, to)). */
export function findPlaybackMapRangeContaining(
  seekMs: number,
  ranges: PlaybackMapRange[]
): PlaybackMapRange | null {
  if (!ranges?.length || !Number.isFinite(seekMs)) {
    return null;
  }
  for (const r of ranges) {
    const startMs = parsePlaybackMapTokenToMillis(r.from);
    const endMs = parsePlaybackMapTokenToMillis(r.to);
    if (seekMs >= startMs && seekMs < endMs) {
      return r;
    }
  }
  return null;
}

/** Segments overlapping [startMs, endMs]. */
export function filterPlaybackMapRangesOverlapping(
  ranges: PlaybackMapRange[],
  startMs: number,
  endMs: number
): PlaybackMapRange[] {
  if (!ranges?.length || endMs <= startMs) {
    return [];
  }
  return ranges.filter((r) => {
    const a = parsePlaybackMapTokenToMillis(r.from);
    const b = parsePlaybackMapTokenToMillis(r.to);
    return b > startMs && a < endMs;
  });
}

/** Camera GMT wall-clock `YYYYMMDD-HHmm` for WebSocket playback seek (`…REC…`). */
export function formatPlaybackSeekToken(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const h = String(d.getUTCHours()).padStart(2, '0');
  const min = String(d.getUTCMinutes()).padStart(2, '0');
  return `${y}${m}${day}-${h}${min}`;
}

/** Camera GMT date/time parts for alert clip filenames `alert-{id}-{YYYYMMDD}-{HHmmss}.mp4`. */
export function formatAlertClipFilenameParts(d: Date): { dateStr: string; timeStr: string } {
  const dateStr =
    `${d.getUTCFullYear()}` +
    `${String(d.getUTCMonth() + 1).padStart(2, '0')}` +
    `${String(d.getUTCDate()).padStart(2, '0')}`;
  const timeStr =
    `${String(d.getUTCHours()).padStart(2, '0')}` +
    `${String(d.getUTCMinutes()).padStart(2, '0')}` +
    `${String(d.getUTCSeconds()).padStart(2, '0')}`;
  return { dateStr, timeStr };
}

/** Parse camera GMT token `YYYYMMDD-HHmmss` to epoch ms (timeline math; UI still formats locally). */
export function parsePlaybackMapTokenToMillis(token: string): number {
  const [datePart, timePart] = token.split('-');
  const y = +datePart.slice(0, 4);
  const mo = +datePart.slice(4, 6) - 1;
  const d = +datePart.slice(6, 8);
  const hh = +timePart.slice(0, 2);
  const mm = +timePart.slice(2, 4);
  const ss = timePart.length >= 6 ? +timePart.slice(4, 6) : 0;
  return Date.UTC(y, mo, d, hh, mm, ss);
}

// WebRTC configuration
const offerOptions = {
  offerToReceiveAudio: true,
  offerToReceiveVideo: true
};

const peerConnectionConfig = {
  iceServers: [
    {
      urls: turnServerUrl,
      username: turnUsername,
      credential: turnPassword
    },
    {
      urls: stunServerUrl
    }
  ]
};

// Wake-up configuration
const WAKEUP_CONFIG = {
  retryIntervalMs: 15000,
  timeoutMs: 120000,
  countdownIntervalMs: 1000
};

/** Max wait for `playback-map-response` (camera wake + map download can exceed 1 min). */
const PLAYBACK_MAP_RESPONSE_TIMEOUT_MS = 120000;

@Injectable({
  providedIn: 'root'
})
export class VideoService {
  public type: string = "stream";
  public path: Path;
  public peerConnection: RTCPeerConnection;
  public remoteVideo: ElementRef;
  public socket$: WebSocketSubject<Message>;
  public messagesSubject$ = new Subject<any>();
  public messages$ = this.messagesSubject$.asObservable();

  private readonly playbackMapResponseSubject$ = new Subject<PlaybackMapResponsePayload>();
  /** Emits each `playback-map-response` from the signaling server (filter by start/end if needed). */
  public readonly playbackMapResponse$ = this.playbackMapResponseSubject$.asObservable();
  /** Fired after `switch-cam` (same WebRTC session; loadeddata may not fire again). */
  private readonly playbackSourceSwitchSubject$ = new Subject<string>();
  public readonly playbackSourceSwitch$ = this.playbackSourceSwitchSubject$.asObservable();
  public messages: string[] = [];
  public myUsername: string;
  public vehicule: Vehicule;
  private messageSubscription: Subscription;

  // PingV2 properties
  private pingInterval: any = null;
  private pingStartTimer: any = null;
  private readonly PING_INTERVAL_MS = 20000;
  /** Delay first keepalive so `register-client` (pingv2) can settle before `event:ping`. */
  private readonly PING_FIRST_DELAY_MS = 1500;
  private socketSubscription: Subscription | null = null;
  private iceDisconnectTimer: any = null;
  private readonly ICE_DISCONNECT_GRACE_MS = 10000;
  private candidateStatsInterval: any = null;
  private readonly CANDIDATE_STATS_INTERVAL_MS = 3000;
  private lastLoggedCandidatePairId: string | null = null;

  // Wake-up properties
  private wakeupRetryInterval: any = null;
  private wakeupTimeoutTimer: any = null;
  private wakeupCountdownInterval: any = null;
  private wakeupStartTime: number = 0;

  /**
   * True only after signaling-server `wake-up` (camera asleep).
   * If no wake-up is received after video-request, the camera is treated as online — no retries.
   */
  private cameraRequiresWakeup = false;

  // Current request info
  private currentCamera: string = '';
  private currentInfos: string = '';
  private currentIsPlayback: boolean = false;

  // Wake-up saved data
  private wakeupPurpose: 'video' | 'playback-map' | 'camera-config' | null = null;
  /** Last `camera-config` command to retry while signaling-server reports camera is waking. */
  private lastCameraConfigCommand: string | null = null;
  private wakeupSavedCamera: string = '';
  private wakeupSavedInfos: string = '';
  private wakeupSavedIsPlayback: boolean = false;

  /** In-flight playback-map download (supports camera wake-up retries). */
  private pendingPlaybackMap: {
    start: string;
    end: string;
    resolve: (data: PlaybackMapResponsePayload) => void;
    reject: (err: Error) => void;
    timeoutTimer: ReturnType<typeof setTimeout> | null;
    responseSub: Subscription | null;
  } | null = null;

  // Wake-up observables
  private isWakingUpSubject$ = new BehaviorSubject<boolean>(false);
  public isWakingUp$ = this.isWakingUpSubject$.asObservable();

  private wakeupTimeRemainingSubject$ = new BehaviorSubject<number>(0);
  public wakeupTimeRemaining$ = this.wakeupTimeRemainingSubject$.asObservable();

  // ============================================================================
  // UNIFIED VIDEO ELEMENT MANAGEMENT
  // ============================================================================

  private videoElementState = {
    isReady: false,
    elementRef: null as ElementRef<HTMLVideoElement> | null,
    bufferedEvents: [] as RTCTrackEvent[],
    monitorInterval: null as any,
    maxBufferSize: 10
  };

  public pingPong: boolean = false;
  public stream: boolean = false;
  public playback: boolean = false;
  public socketLoading: boolean = false;
  public counterLoading: boolean = false;
  public connexion: boolean = false;
  public loadingStream: boolean = false;
  /** Set after the first video-request of the current session (not switch-cam). */
  private videoRequestSent = false;
  /** True from video-offer received until track received - do not send hang-up or video-request during this time. */
  private isNegotiating: boolean = false;
  public camChuaffeur: boolean = false;
  public camFrontFacing: boolean = false;
  public camRearFacing: boolean = false;
  public showVideoModal: boolean = false;

  /** GMT seek token `YYYYMMDD-HHmm` for historical playback (overrides path date). */
  public playbackSeekToken: string | null = null;
  /** Active playback-map segment (fleet-12 `recordingRange`). */
  public recordingRange: PlaybackMapRange | null = null;
  /** Cached playback-map ranges for chunk alignment (fleet-12 `playbackMapRanges`). */
  public playbackMapRanges: PlaybackMapRange[] = [];
  public returnRoute = '/client';
  public counter: number = 0;
  private countdownInterval: any;

  constructor(
    public dataManagementService: DataManagementService,
    private toastController: ToastController,
    private translate: TranslateService
  ) {
    this.vehicule = this.dataManagementService.selectedVehicule;
    this.createPeerConnection();
    this.startVideoElementMonitor();
  }

  // ============================================================================
  // VIDEO ELEMENT MANAGEMENT - UNIFIED FOR BOTH COMPONENTS
  // ============================================================================

  /**
   * Set video element for both VideoComponent and TrackControlComponent
   */
  public setVideoElement(videoRef: ElementRef<HTMLVideoElement>): boolean {
    if (!this.isVideoElementValid(videoRef)) {
      return false;
    }

    const previousElement = this.videoElementState.elementRef?.nativeElement;
    const existingStream = (previousElement?.srcObject ?? null) as MediaStream | null;

    this.remoteVideo = videoRef;
    this.videoElementState.elementRef = videoRef;
    this.configureVideoElement(videoRef.nativeElement);
    this.videoElementState.isReady = true;

    if (existingStream && videoRef.nativeElement.srcObject !== existingStream) {
      videoRef.nativeElement.srcObject = existingStream;
      videoRef.nativeElement.autoplay = true;
      console.log('Transferred MediaStream to new video element');
      if (this.currentIsPlayback) {
        this.safeVideoPlay(videoRef.nativeElement);
      }
    }

    console.log('Video element set and ready for streaming');
    this.processBufferedTrackEvents();
    return true;
  }

  /**
   * Check if video element is valid
   */
  private isVideoElementValid(videoRef: ElementRef<HTMLVideoElement>): boolean {
    if (!videoRef?.nativeElement) {
      console.error('Video reference is invalid');
      return false;
    }

    const videoElement = videoRef.nativeElement;

    if (videoElement.tagName !== 'VIDEO') {
      console.error('Element is not a video tag:', videoElement.tagName);
      return false;
    }

    if (!document.body.contains(videoElement) && !document.contains(videoElement)) {
      // Ionic can briefly detach nodes during view transitions; still allow binding.
      console.warn('Video element not yet attached to document; continuing setup');
    }

    if (typeof videoElement.play !== 'function') {
      console.error('Video element does not have play method');
      return false;
    }

    return true;
  }

  /**
   * Configure video element for streaming
   */
  private configureVideoElement(videoElement: HTMLVideoElement): void {
    try {
      videoElement.autoplay = true;
      videoElement.playsInline = true;
      videoElement.setAttribute('playsinline', 'true');
      videoElement.setAttribute('webkit-playsinline', 'true');
      // Start muted so mobile browsers allow autoplay; unmute after play succeeds.
      videoElement.muted = true;
      videoElement.controls = false;
      videoElement.removeAttribute('controls');
      videoElement.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
      videoElement.setAttribute('disablepictureinpicture', '');
      videoElement.disablePictureInPicture = true;
      videoElement.preload = 'none';

      // Set up error handling
      videoElement.onerror = (e) => {
        console.error('Video element native error:', e);
        this.videoElementState.isReady = false;
      };

      videoElement.onloadstart = () => {
        console.log('Video loading started');
      };

      videoElement.onloadeddata = () => {
        console.log('Video data loaded');
      };

      videoElement.onplaying = () => {
        console.log('Video playing successfully');
      };

    } catch (error) {
      console.warn('Video element configuration completed with warnings:', error);
    }
  }

  /**
   * Start monitoring video element state
   */
  private startVideoElementMonitor(): void {
    // Check every 500ms
    this.videoElementState.monitorInterval = setInterval(() => {
      this.checkVideoElementState();
    }, 500);
  }

  /**
   * Check and update video element state
   */
  private checkVideoElementState(): void {
    if (!this.videoElementState.elementRef?.nativeElement) {
      this.videoElementState.isReady = false;
      return;
    }

    const videoElement = this.videoElementState.elementRef.nativeElement;
    const isValid = videoElement &&
      videoElement.tagName === 'VIDEO' &&
      document.contains(videoElement) &&
      typeof videoElement.play === 'function';

    this.videoElementState.isReady = isValid;
  }

  /**
   * Buffer track event if video element isn't ready
   */
  private bufferTrackEvent(event: RTCTrackEvent): void {
    if (this.videoElementState.bufferedEvents.length >= this.videoElementState.maxBufferSize) {
      this.videoElementState.bufferedEvents.shift();
    }

    this.videoElementState.bufferedEvents.push(event);
    console.log(`Track event buffered. Buffer size: ${this.videoElementState.bufferedEvents.length}`);
  }

  /**
   * Process buffered track events when video becomes ready
   */
  private processBufferedTrackEvents(): void {
    if (this.videoElementState.bufferedEvents.length === 0) return;

    console.log(`Processing ${this.videoElementState.bufferedEvents.length} buffered track events`);

    while (this.videoElementState.bufferedEvents.length > 0) {
      const event = this.videoElementState.bufferedEvents.shift();
      this.handleTrackEventImmediately(event);
    }
  }

  /**
   * Public method to check if video element is ready
   */
  public isVideoElementReady(): boolean {
    return this.videoElementState.isReady;
  }

  /**
   * Public method to get video element status
   */
  public getVideoElementStatus(): string {
    if (!this.videoElementState.elementRef) return 'No video element set';
    if (!this.videoElementState.isReady) return 'Video element not ready';
    return 'Video element ready for streaming';
  }

  // ============================================================================
  // TRACK EVENT HANDLING - ALWAYS READY
  // ============================================================================

  private handleTrackEvent = (event: RTCTrackEvent) => {
    console.log("Track event received - video stream starting");
    this.handleTrackEventImmediately(event);
  }

  private handleTrackEventImmediately(event: RTCTrackEvent): void {
    if (!this.videoElementState.isReady || !this.remoteVideo?.nativeElement) {
      console.warn('Video element not ready, buffering track event');
      this.bufferTrackEvent(event);
      return;
    }

    const videoElement = this.remoteVideo.nativeElement;

    try {
      // Clear wake-up if it was in progress
      if (this.isCurrentlyWakingUp()) {
        this.handleWakeupSuccess();
      }

      let remoteStream: MediaStream | null = event.streams?.[0] ?? null;
      if (!remoteStream && event.track) {
        remoteStream = new MediaStream([event.track]);
      }
      if (!remoteStream) {
        console.error("No streams in track event");
        this.displayStatus(this.t('VIDEO.error.noVideoStream'), 'error');
        return;
      }

      // Prefer attaching without destructive load() — MediaStream + load() races often
      // leave Capacitor WebViews with ICE completed but a permanently black <video>.
      this.removeVideoStreamListeners(videoElement);
      this.configureVideoElement(videoElement);
      videoElement.srcObject = remoteStream;

      // Remote tracks often start muted until the first RTP keyframe arrives.
      this.bindRemoteTrackLifecycle(remoteStream);

      // Set up event listeners for this stream
      this.setupVideoStreamListeners(videoElement);

      // Mark session active early so the <video> container becomes visible on mobile
      // (hidden display:none elements often never fire loadeddata/playing).
      this.loadingStream = true;
      if (this.currentIsPlayback) {
        this.playback = true;
      } else {
        this.stream = true;
      }

      console.log('Track event successfully processed - video stream attached', {
        trackCount: remoteStream.getTracks().length,
        videoTracks: remoteStream.getVideoTracks().map(t => ({
          id: t.id,
          muted: t.muted,
          enabled: t.enabled,
          readyState: t.readyState,
        })),
      });

    } catch (error) {
      console.error('Error in immediate track event handling:', error);
      this.bufferTrackEvent(event);
    }
  }

  /**
   * Safe video element reset without destroying the element
   */
  private safeVideoElementReset(): void {
    if (!this.remoteVideo?.nativeElement) return;

    const videoElement = this.remoteVideo.nativeElement;

    try {
      // Remove existing event listeners
      this.removeVideoStreamListeners(videoElement);
      this.unbindRemoteTrackLifecycle(videoElement.srcObject as MediaStream | null);

      // Reset video state — avoid load() with MediaStreams (WebView black-screen bug)
      videoElement.pause();
      videoElement.srcObject = null;
      videoElement.removeAttribute('src');

      // Re-apply essential properties (muted first so mobile autoplay is allowed)
      videoElement.autoplay = true;
      videoElement.playsInline = true;
      videoElement.setAttribute('playsinline', 'true');
      videoElement.setAttribute('webkit-playsinline', 'true');
      videoElement.muted = true;

    } catch (error) {
      console.warn('Safe video reset completed with warnings:', error);
    }
  }

  /**
   * When ICE is up but frames were delayed/missed, re-bind receiver tracks and retry play.
   */
  private recoverRemoteVideoPlayback(reason: string): void {
    if (!this.peerConnection || !this.remoteVideo?.nativeElement) {
      return;
    }

    const videoElement = this.remoteVideo.nativeElement;
    const videoTracks = this.peerConnection
      .getReceivers()
      .map(r => r.track)
      .filter((t): t is MediaStreamTrack => !!t && t.kind === 'video' && t.readyState === 'live');

    if (!videoTracks.length) {
      console.warn(`[WebRTC] recover (${reason}): no live video tracks yet`);
      return;
    }

    let stream = videoElement.srcObject as MediaStream | null;
    if (!stream) {
      stream = new MediaStream(videoTracks);
      videoElement.srcObject = stream;
      this.bindRemoteTrackLifecycle(stream);
      this.setupVideoStreamListeners(videoElement);
      console.log(`[WebRTC] recover (${reason}): attached ${videoTracks.length} track(s)`);
    } else {
      for (const track of videoTracks) {
        if (!stream.getTracks().some(t => t.id === track.id)) {
          stream.addTrack(track);
        }
      }
    }

    this.loadingStream = true;
    if (this.currentIsPlayback) {
      this.playback = true;
    } else {
      this.stream = true;
    }

    this.configureVideoElement(videoElement);
    this.safeVideoPlay(videoElement);

    // Retry once more after a short delay — keyframes often arrive after ICE completes.
    setTimeout(() => {
      if (!this.remoteVideo?.nativeElement) {
        return;
      }
      const el = this.remoteVideo.nativeElement;
      if (el.videoWidth > 0 && !el.paused) {
        this.loadingStream = false;
        this.isNegotiating = false;
        return;
      }
      console.log(`[WebRTC] recover (${reason}): retry play (videoWidth=${el.videoWidth}, paused=${el.paused})`);
      this.safeVideoPlay(el);
    }, 400);
  }

  private bindRemoteTrackLifecycle(stream: MediaStream): void {
    for (const track of stream.getVideoTracks()) {
      const onUnmute = () => {
        console.log('[WebRTC] remote video track unmuted (frames arriving)');
        this.recoverRemoteVideoPlayback('track-unmute');
      };
      const onMute = () => {
        console.warn('[WebRTC] remote video track muted');
      };
      const onEnded = () => {
        console.warn('[WebRTC] remote video track ended');
      };
      track.addEventListener('unmute', onUnmute);
      track.addEventListener('mute', onMute);
      track.addEventListener('ended', onEnded);
      (track as any)._lifecycleListeners = { onUnmute, onMute, onEnded };
    }
  }

  private unbindRemoteTrackLifecycle(stream: MediaStream | null): void {
    if (!stream) {
      return;
    }
    for (const track of stream.getVideoTracks()) {
      const listeners = (track as any)._lifecycleListeners;
      if (!listeners) {
        continue;
      }
      track.removeEventListener('unmute', listeners.onUnmute);
      track.removeEventListener('mute', listeners.onMute);
      track.removeEventListener('ended', listeners.onEnded);
      delete (track as any)._lifecycleListeners;
    }
  }

  /**
   * Set up video stream event listeners
   */
  private setupVideoStreamListeners(videoElement: HTMLVideoElement): void {
    this.removeVideoStreamListeners(videoElement);

    const onLoadStart = () => {
      console.log('Video loading started');
      this.displayStatus(this.t('VIDEO.stream.loading'), 'info');
    };

    const onLoadedData = () => {
      console.log('Video data loaded', {
        videoWidth: videoElement.videoWidth,
        videoHeight: videoElement.videoHeight,
      });
      this.loadingStream = false;
      this.isNegotiating = false;
      if (this.currentIsPlayback) {
        this.playback = true;
      } else {
        this.stream = true;
      }
      this.displayStatus(this.t('VIDEO.stream.connected'), 'success');
      this.safeVideoPlay(videoElement);
    };

    const onError = (e: any) => {
      console.error('Video element error:', e);
      this.loadingStream = false;
      this.isNegotiating = false;
      this.displayStatus(this.t('VIDEO.error.videoPlayback'), 'error');
    };

    const onPlaying = () => {
      console.log('Video playback started successfully', {
        videoWidth: videoElement.videoWidth,
        videoHeight: videoElement.videoHeight,
      });
      // Clear loading state when video actually plays (fixes overlay stuck on camera switch)
      this.loadingStream = false;
      this.isNegotiating = false;
    };

    const onResize = () => {
      if (videoElement.videoWidth > 0) {
        console.log('Video frame size available', {
          videoWidth: videoElement.videoWidth,
          videoHeight: videoElement.videoHeight,
        });
        this.loadingStream = false;
        this.isNegotiating = false;
      }
    };

    // Add listeners
    videoElement.addEventListener('loadstart', onLoadStart);
    videoElement.addEventListener('loadeddata', onLoadedData);
    videoElement.addEventListener('error', onError);
    videoElement.addEventListener('playing', onPlaying);
    videoElement.addEventListener('resize', onResize);

    // Store references for cleanup
    (videoElement as any)._videoListeners = { onLoadStart, onLoadedData, onError, onPlaying, onResize };

    // Attempt to play
    this.safeVideoPlay(videoElement);
  }

  /**
   * Remove video stream event listeners
   */
  private removeVideoStreamListeners(videoElement: HTMLVideoElement): void {
    if (!videoElement) return;

    try {
      const listeners = (videoElement as any)._videoListeners;
      if (listeners) {
        videoElement.removeEventListener('loadstart', listeners.onLoadStart);
        videoElement.removeEventListener('loadeddata', listeners.onLoadedData);
        videoElement.removeEventListener('error', listeners.onError);
        videoElement.removeEventListener('playing', listeners.onPlaying);
        if (listeners.onResize) {
          videoElement.removeEventListener('resize', listeners.onResize);
        }
        delete (videoElement as any)._videoListeners;
      }
    } catch (error) {
      // Ignore errors during listener removal
    }
  }

  /**
   * Safe video play with comprehensive error handling
   */
  private safeVideoPlay(videoElement: HTMLVideoElement): void {
    if (!videoElement || typeof videoElement.play !== 'function') return;

    // Keep muted on Capacitor/WebView. Auto-unmuting after play() often pauses
    // playback or leaves a black frame until a user gesture.
    videoElement.muted = true;
    videoElement.autoplay = true;
    videoElement.playsInline = true;
    videoElement.setAttribute('playsinline', 'true');
    videoElement.setAttribute('webkit-playsinline', 'true');

    const playPromise = videoElement.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log('Video playback started successfully (muted)');
        })
        .catch(error => {
          console.warn('Autoplay was prevented:', error);
          this.displayStatus(this.t('VIDEO.error.autoplayBlocked'), 'warning');
          videoElement.muted = true;
          videoElement.play().catch(() => undefined);
        });
    }
  }

  // ============================================================================
  // WAKE-UP MECHANISM (only while signaling-server reports camera is waking)
  // ============================================================================

  private resetCameraWakeupExpectation(): void {
    this.cameraRequiresWakeup = false;
  }

  private startWakeupRetry(camera: string, infos: string, isPlayback: boolean): void {
    if (!this.cameraRequiresWakeup) {
      return;
    }
    console.log('Starting wake-up retry mechanism (15s interval)');

    // Clear any existing intervals FIRST
    this.clearWakeupIntervals();

    this.wakeupPurpose = 'video';
    // Store wake-up request info for retries
    this.wakeupSavedCamera = camera;
    this.wakeupSavedInfos = infos;
    this.wakeupSavedIsPlayback = isPlayback;

    // Set wake-up state
    this.isWakingUpSubject$.next(true);
    this.wakeupStartTime = Date.now();
    this.wakeupTimeRemainingSubject$.next(Math.ceil(WAKEUP_CONFIG.timeoutMs / 1000));

    // Start countdown timer
    this.wakeupCountdownInterval = setInterval(() => {
      const elapsed = Date.now() - this.wakeupStartTime;
      const remaining = Math.ceil((WAKEUP_CONFIG.timeoutMs - elapsed) / 1000);
      this.wakeupTimeRemainingSubject$.next(Math.max(0, remaining));
    }, WAKEUP_CONFIG.countdownIntervalMs);

    // Start retry interval (only while server indicated camera is waking)
    this.wakeupRetryInterval = setInterval(() => {
      if (this.isWakingUpSubject$.value && this.cameraRequiresWakeup) {
        console.log(this.t('VIDEO.wakeup.retrying') + ' (15s interval)');
        this.retryVideoRequest();
      } else {
        this.clearWakeupIntervals();
      }
    }, WAKEUP_CONFIG.retryIntervalMs);

    // Set timeout
    this.wakeupTimeoutTimer = setTimeout(() => {
      if (this.isWakingUpSubject$.value) {
        console.error(this.t('VIDEO.wakeup.failed'));
        this.handleWakeupTimeout();
      }
    }, WAKEUP_CONFIG.timeoutMs);

    // Send first retry immediately
    this.retryVideoRequest();

    this.displayStatus(this.t('VIDEO.wakeup.message') + ' (retry every 15s)', 'info');
  }

  private clearWakeupIntervals(): void {
    if (this.wakeupRetryInterval) {
      clearInterval(this.wakeupRetryInterval);
      this.wakeupRetryInterval = null;
    }

    if (this.wakeupTimeoutTimer) {
      clearTimeout(this.wakeupTimeoutTimer);
      this.wakeupTimeoutTimer = null;
    }

    if (this.wakeupCountdownInterval) {
      clearInterval(this.wakeupCountdownInterval);
      this.wakeupCountdownInterval = null;
    }
  }

  private retryVideoRequest(): void {
    if (!this.cameraRequiresWakeup || !this.wakeupSavedInfos) {
      return;
    }

    // Never send video-request during ICE negotiation - would interrupt the dashcam
    if (this.isNegotiating) {
      console.log('Skipping video-request retry: ICE negotiation in progress');
      return;
    }

    console.log('Sending video-request retry with infos:', this.wakeupSavedInfos);

    this.sendMessage({
      event: 'message',
      data: {
        source: this.myUsername,
        type: 'video-request',
        infos: this.wakeupSavedInfos,
        target: this.vehicule.device?.streamId
      }
    });
  }

  public clearWakeupRetry(): void {
    console.log('Clearing wake-up retry mechanism');
    this.clearWakeupIntervals();
    this.resetCameraWakeupExpectation();
    this.isWakingUpSubject$.next(false);
    this.wakeupTimeRemainingSubject$.next(0);
    this.wakeupStartTime = 0;
    if (this.wakeupPurpose !== 'playback-map' && this.wakeupPurpose !== 'camera-config') {
      this.wakeupSavedCamera = '';
      this.wakeupSavedInfos = '';
      this.wakeupSavedIsPlayback = false;
    }
    if (this.wakeupPurpose !== 'camera-config') {
      this.lastCameraConfigCommand = null;
    }
    this.wakeupPurpose = null;
  }

  /** Mobile historical page cleanup: cancel in-flight playback-map + wake retries. */
  public teardownPlaybackMapRequests(): void {
    this.cancelPendingPlaybackMap(new Error('cancelled'));
    this.clearWakeupRetry();
  }

  /** Camera finished waking (stream or map ready) — stop wake retries. */
  private handleWakeupSuccess(): void {
    if (this.wakeupPurpose === 'playback-map' || this.wakeupPurpose === 'camera-config') {
      return;
    }
    console.log('Camera wake-up complete, stream starting');
    this.clearWakeupIntervals();
    this.isWakingUpSubject$.next(false);
    this.wakeupTimeRemainingSubject$.next(0);
    this.resetCameraWakeupExpectation();
    this.displayStatus(this.t('VIDEO.wakeup.success'), 'success');
  }

  private startWakeupRetryForPlaybackMap(): void {
    if (!this.pendingPlaybackMap || !this.cameraRequiresWakeup) {
      return;
    }
    console.log('Starting playback-map wake-up retry (15s interval)');
    this.clearWakeupIntervals();
    this.wakeupPurpose = 'playback-map';
    this.isWakingUpSubject$.next(true);
    this.wakeupStartTime = Date.now();
    this.wakeupTimeRemainingSubject$.next(Math.ceil(WAKEUP_CONFIG.timeoutMs / 1000));

    this.wakeupCountdownInterval = setInterval(() => {
      const elapsed = Date.now() - this.wakeupStartTime;
      const remaining = Math.ceil((WAKEUP_CONFIG.timeoutMs - elapsed) / 1000);
      this.wakeupTimeRemainingSubject$.next(Math.max(0, remaining));
    }, WAKEUP_CONFIG.countdownIntervalMs);

    this.wakeupRetryInterval = setInterval(() => {
      if (this.isWakingUpSubject$.value && this.cameraRequiresWakeup) {
        console.log('Retrying playback-map (camera wake)');
        this.retryPlaybackMapWakeCycle();
      } else {
        this.clearWakeupIntervals();
      }
    }, WAKEUP_CONFIG.retryIntervalMs);

    this.wakeupTimeoutTimer = setTimeout(() => {
      if (this.isWakingUpSubject$.value && this.wakeupPurpose === 'playback-map') {
        this.handlePlaybackMapWakeupTimeout();
      }
    }, WAKEUP_CONFIG.timeoutMs);

    this.retryPlaybackMapWakeCycle();
    this.displayStatus(this.t('VIDEO.wakeup.playbackMapMessage'), 'info');
  }

  private preparePlaybackMapWakeupSession(): void {
    this.resetCameraWakeupExpectation();
  }

  /** Re-request recording map while signaling-server reports camera is waking (no fixed S4/S5 video-request). */
  private retryPlaybackMapWakeCycle(): void {
    if (!this.cameraRequiresWakeup) {
      return;
    }
    this.retryPlaybackMapCommand();
  }

  /** Hang up WebRTC if wake stream started while fetching the map (keep signaling socket). */

  private startWakeupRetryForCameraConfig(): void {
    if (!this.lastCameraConfigCommand || !this.cameraRequiresWakeup) {
      return;
    }
    if (!this.vehicule?.device?.streamId) {
      return;
    }
    console.log('Starting camera-config wake-up retry (15s interval)');
    this.clearWakeupIntervals();
    this.wakeupPurpose = 'camera-config';
    this.isWakingUpSubject$.next(true);
    this.wakeupStartTime = Date.now();
    this.wakeupTimeRemainingSubject$.next(Math.ceil(WAKEUP_CONFIG.timeoutMs / 1000));

    this.wakeupCountdownInterval = setInterval(() => {
      const elapsed = Date.now() - this.wakeupStartTime;
      const remaining = Math.ceil((WAKEUP_CONFIG.timeoutMs - elapsed) / 1000);
      this.wakeupTimeRemainingSubject$.next(Math.max(0, remaining));
    }, WAKEUP_CONFIG.countdownIntervalMs);

    this.wakeupRetryInterval = setInterval(() => {
      if (this.isWakingUpSubject$.value && this.cameraRequiresWakeup) {
        console.log('Retrying camera-config (camera wake)');
        this.retryCameraConfigCommand();
      } else {
        this.clearWakeupIntervals();
      }
    }, WAKEUP_CONFIG.retryIntervalMs);

    this.wakeupTimeoutTimer = setTimeout(() => {
      if (this.isWakingUpSubject$.value && this.wakeupPurpose === 'camera-config') {
        this.handleCameraConfigWakeupTimeout();
      }
    }, WAKEUP_CONFIG.timeoutMs);

    this.retryCameraConfigCommand();
    this.displayStatus(this.t('VIDEO.wakeup.configMessage'), 'info');
  }

  private retryCameraConfigCommand(): void {
    if (!this.cameraRequiresWakeup || !this.lastCameraConfigCommand || !this.vehicule?.device?.streamId) {
      return;
    }
    this.sendCameraConfigPayload(this.lastCameraConfigCommand, this.vehicule.device.streamId);
  }

  private handleCameraConfigWakeupTimeout(): void {
    console.error('camera-config wake-up timeout');
    this.displayStatus(this.t('VIDEO.wakeup.timeout'), 'error');
    this.clearWakeupRetry();
  }

  private handleCameraConfigResponseReceived(): void {
    if (this.wakeupPurpose !== 'camera-config') {
      return;
    }
    this.clearWakeupIntervals();
    this.isWakingUpSubject$.next(false);
    this.wakeupTimeRemainingSubject$.next(0);
    this.resetCameraWakeupExpectation();
    this.wakeupPurpose = null;
    this.displayStatus(this.t('VIDEO.wakeup.configSuccess'), 'success');
  }

  public sendCameraConfig(command: string, streamId: string): void {
    if (!this.socket$ || !this.connexion || !this.myUsername) {
      console.warn('Cannot send camera-config: signaling not connected');
      return;
    }
    const trimmed = (command || '').trim();
    if (!trimmed || !streamId) {
      return;
    }
    this.lastCameraConfigCommand = trimmed;
    this.resetCameraWakeupExpectation();
    this.sendCameraConfigPayload(trimmed, streamId);
  }

  public clearCameraConfigSession(): void {
    this.lastCameraConfigCommand = null;
    if (this.wakeupPurpose === 'camera-config') {
      this.clearWakeupRetry();
    }
  }

  private sendCameraConfigPayload(command: string, streamId: string): void {
    this.sendMessage({
      event: 'message',
      data: {
        source: this.myUsername,
        target: streamId,
        type: 'camera-config',
        infos: command
      }
    });
  }

  private releaseStreamOpenedForPlaybackMapWake(): void {
    if (!this.stream && !this.playback && !this.isNegotiating && !this.loadingStream) {
      return;
    }
    const vehicule = this.vehicule;
    if (vehicule?.device?.streamId && this.socket$ && this.connexion && !this.loadingStream) {
      try {
        this.sendMessage({
          event: 'message',
          data: {
            source: this.myUsername,
            target: vehicule.device.streamId,
            type: 'hang-up'
          }
        });
      } catch (e) {
        console.warn('hang-up after playback-map wake failed', e);
      }
    }
    this.closeVideoCall();
  }

  private handlePlaybackMapWakeupTimeout(): void {
    console.error('playback-map wake-up timeout');
    this.displayStatus(this.t('VIDEO.wakeup.timeout'), 'error');
    this.cancelPendingPlaybackMap(new Error('camera wake-up timeout'));
  }

  private retryPlaybackMapCommand(): void {
    const pending = this.pendingPlaybackMap;
    if (!pending) {
      return;
    }
    this.sendPlaybackMapCommand(pending.start, pending.end);
  }

  private sendPlaybackMapCommand(start: string, end: string): void {
    if (!this.socket$ || !this.connexion || !this.myUsername) {
      console.warn('Cannot send playback-map: signaling not connected');
      return;
    }
    if (!this.vehicule?.device?.streamId) {
      console.warn('Cannot send playback-map: no stream id');
      return;
    }
    this.sendMessage({
      event: 'message',
      data: {
        source: this.myUsername,
        target: this.vehicule.device.streamId,
        type: 'playback-map',
        command: `playback-map ${start} ${end}`
      }
    });
  }

  private tryCompletePendingPlaybackMap(data: PlaybackMapResponsePayload): void {
    const pending = this.pendingPlaybackMap;
    if (!pending || data.start !== pending.start || data.end !== pending.end) {
      return;
    }
    if (data.status !== 0) {
      this.cancelPendingPlaybackMap(new Error(`playback-map status ${data.status}`));
      return;
    }
    this.finishPendingPlaybackMapSuccess(data);
  }

  private finishPendingPlaybackMapSuccess(data: PlaybackMapResponsePayload): void {
    const pending = this.pendingPlaybackMap;
    if (!pending) {
      return;
    }
    if (pending.timeoutTimer) {
      clearTimeout(pending.timeoutTimer);
    }
    if (pending.responseSub) {
      pending.responseSub.unsubscribe();
    }
    this.pendingPlaybackMap = null;
    if (this.wakeupPurpose === 'playback-map') {
      this.clearWakeupIntervals();
      this.isWakingUpSubject$.next(false);
      this.wakeupTimeRemainingSubject$.next(0);
      this.resetCameraWakeupExpectation();
      this.wakeupPurpose = null;
      this.wakeupSavedInfos = '';
      this.wakeupSavedCamera = '';
      this.currentInfos = '';
      this.currentCamera = '';
      this.displayStatus(this.t('VIDEO.wakeup.playbackMapSuccess'), 'success');
    }
    this.releaseStreamOpenedForPlaybackMapWake();
    pending.resolve(data);
  }

  private cancelPendingPlaybackMap(err: Error): void {
    const pending = this.pendingPlaybackMap;
    if (!pending) {
      return;
    }
    if (pending.timeoutTimer) {
      clearTimeout(pending.timeoutTimer);
    }
    if (pending.responseSub) {
      pending.responseSub.unsubscribe();
    }
    const reject = pending.reject;
    this.pendingPlaybackMap = null;
    this.clearWakeupRetry();
    this.releaseStreamOpenedForPlaybackMapWake();
    reject(err);
  }

  private handleWakeupTimeout(): void {
    console.error(this.t('VIDEO.wakeup.failed'));
    this.clearWakeupRetry();
    this.loadingStream = false;
    this.isNegotiating = false;
    this.displayStatus(this.t('VIDEO.wakeup.timeout'), 'error');
  }

  public isCurrentlyWakingUp(): boolean {
    return this.isWakingUpSubject$.value;
  }

  public getWakeupTimeRemaining(): number {
    return this.wakeupTimeRemainingSubject$.value;
  }

  // ============================================================================
  // EXISTING METHODS (Updated to use translations)
  // ============================================================================

  private t(key: string): string {
    return this.translate.instant(key);
  }

  getBrowserId(): string {
    let browserId = localStorage.getItem('browser_id');
    if (!browserId) {
      browserId = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `br-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('browser_id', browserId);
    }
    return browserId;
  }

  async displayStatus(message: string, type: 'success' | 'info' | 'warning' | 'error' = 'info') {
    let color: string;
    switch (type) {
      case 'success':
        color = 'success';
        break;
      case 'warning':
        color = 'warning';
        break;
      case 'error':
        color = 'danger';
        break;
      default:
        color = 'primary';
        break;
    }
    const toast = await this.toastController.create({
      message,
      duration: 4000,
      position: 'bottom',
      color,
      header: this.t('VIDEO.title'),
      buttons: [{ text: 'X', role: 'cancel' }]
    });
    await toast.present();
  }

  private safeStringifyForLog(payload: any): string {
    try {
      return JSON.stringify(payload);
    } catch {
      return '[unserializable-payload]';
    }
  }

  private isCameraConfigExchange(msg: any): boolean {
    const msgType = msg?.data?.type;
    const infos = msg?.data?.infos;
    if (msgType === 'video-request' || msgType === 'switch-cam' || msgType === 'video-response') {
      return true;
    }
    return typeof infos === 'string' && infos.length > 0;
  }

  private logSignalingExchange(direction: 'TX' | 'RX', payload: any): void {
    const nowIso = new Date().toISOString();
    const msgType = payload?.data?.type || payload?.event || 'unknown';
    const header = `[SIGNALING ${direction}] ${nowIso} type=${msgType}`;
    console.log(header, this.safeStringifyForLog(payload));
    if (this.isCameraConfigExchange(payload)) {
      console.log(`[CAMERA-CONFIG ${direction}] ${nowIso}`, this.safeStringifyForLog(payload));
    }
  }

  private getNewWebSocket(): WebSocketSubject<any> {
    const socket = webSocket({
      url: wsocket,
      openObserver: {
        next: () => {
          // A raced connect() can supersede this socket before it opens.
          if (this.socket$ !== socket) {
            console.warn('Ignoring open for superseded WebSocket');
            try {
              socket.complete();
            } catch {
              // ignore
            }
            return;
          }
          console.log(this.t('VIDEO.websocket.established'));
          this.connexion = true;
          this.socketLoading = false;
          this.displayStatus(this.t('VIDEO.connection.established'), 'success');
          // Register and start ping only AFTER socket is open (same as cam_simulation)
          if (this.myUsername) {
            this.sendToRegisterServer({
              id: this.myUsername,
              name: this.myUsername,
              date: Date.now(),
              type: 'pingv2',
              peerType: 'mobile'
            });
            this.startPing(this.myUsername);
          }
        }
      },
      closeObserver: {
        next: (event) => {
          // Orphaned/superseded sockets must not clear the active connection + pingv2.
          if (this.socket$ !== socket) {
            console.warn('Ignoring close for superseded WebSocket', event?.reason);
            return;
          }
          console.log(this.t('VIDEO.websocket.closed'), event.reason);
          this.connexion = false;
          this.socketLoading = false;
          this.stopPing();
          this.clearWakeupRetry();
          this.displayStatus(this.t('VIDEO.connection.closedReason') + ': ' + (event.reason || this.t('VIDEO.error.unknownError')), 'warning');
        }
      }
    });
    return socket;
  }

  public connect() {
    // Prevent duplicate sockets: a second connect while the first is still opening
    // was closing pingv2 / connexion via the orphaned socket's closeObserver.
    if (this.connexion && this.socket$) {
      console.warn('connect(): already connected');
      return this.socket$;
    }
    if (this.socketLoading && this.socket$) {
      console.warn('connect(): connection already in progress');
      return this.socket$;
    }

    this.socketLoading = true;

    try {
      if (this.socketSubscription) {
        this.socketSubscription.unsubscribe();
        this.socketSubscription = null;
      }
      if (this.socket$) {
        const previous = this.socket$;
        this.socket$ = null;
        try {
          previous.complete();
        } catch {
          // ignore close errors on previous socket
        }
      }

      const socket = this.getNewWebSocket();
      this.socket$ = socket;

      this.socketSubscription = socket.subscribe(
        (msg: any) => {
          if (this.socket$ !== socket) {
            return;
          }
          this.logSignalingExchange('RX', msg);
          this.messagesSubject$.next(msg);
          this.socketLoading = false;
        },
        (error: any) => {
          if (this.socket$ !== socket) {
            return;
          }
          console.error(this.t('VIDEO.websocket.error'), error);
          this.connexion = false;
          this.socketLoading = false;
          this.stopPing();
          this.clearWakeupRetry();
          this.displayStatus(this.t('VIDEO.connection.error'), 'error');
        },
        () => {
          if (this.socket$ !== socket) {
            return;
          }
          console.log(this.t('VIDEO.websocket.closed'));
          this.connexion = false;
          this.socketLoading = false;
          this.stopPing();
          this.clearWakeupRetry();
          this.displayStatus(this.t('VIDEO.connection.closed'), 'warning');
        }
      );

      return this.socket$;
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.socketLoading = false;
      this.displayStatus(this.t('VIDEO.connection.failed'), 'error');
      return null;
    }
  }

  sendToRegisterServer(msg: any) {
    this.sendMessage({
      event: "register-client",
      data: msg
    });
  }

  public sendMessage(msg: any): void {
    if (!this.socket$) {
      console.error(this.t('VIDEO.websocket.cannotSend'));
      this.displayStatus(this.t('VIDEO.connection.notConnected'), 'error');
      return;
    }

    this.logSignalingExchange('TX', msg);
    this.socket$.next(msg);
  }

  public handleHangupMessage(): void {
    console.log('Received hangup message from peer');
    this.closeVideoCall();
    this.displayStatus(this.t('VIDEO.status.callEndedByPeer'), 'info');
  }

  async handleICECandidateMessage(msg: any): Promise<void> {
    try {
      const candidate = new RTCIceCandidate(msg.data.candidate);
      console.log('Adding received ICE candidate:', JSON.stringify(candidate));
      await this.peerConnection.addIceCandidate(candidate);
    } catch (error) {
      console.error('Error adding ICE candidate:', error);
      this.displayStatus(this.t('VIDEO.error.networkInfo'), 'error');
    }
  }

  async handleVideoOfferMsg(msg: any) {
    try {
      this.isNegotiating = true;
      const desc = new RTCSessionDescription(msg.data.sdp);
      console.log("Setting remote SDP for offer");

      await this.peerConnection.setRemoteDescription(desc);
      console.log("Creating answer");

      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);

      console.log("Sending answer to remote peer");
      this.sendMessage({
        event: "message",
        data: {
          source: this.myUsername,
          target: this.vehicule.device.streamId,
          type: 'video-answer',
          sdp: this.peerConnection.localDescription
        }
      });
    } catch (error) {
      console.error('Error handling video offer:', error);
      this.displayStatus(this.t('VIDEO.error.processingOffer'), 'error');
    }
  }

  async handleVideoAnswerMsg(msg: any) {
    console.log("Call recipient has accepted our call");

    try {
      if (msg.data && msg.data.sdp) {
        const remotedesc = new RTCSessionDescription(msg.data.sdp);
        await this.peerConnection.setRemoteDescription(remotedesc);
        this.displayStatus(this.t('VIDEO.connection.established'), 'success');
      } else {
        console.error('Invalid message format for video answer:', msg);
        this.displayStatus(this.t('VIDEO.error.invalidResponse'), 'error');
      }
    } catch (error) {
      console.error('Error setting remote description:', error);
      this.displayStatus(this.t('VIDEO.error.processingAnswer'), 'error');
    }
  }

  async handleNewICECandidateMsg(msg: any): Promise<void> {
    try {
      const candidate = new RTCIceCandidate(msg.data.candidate);
      console.log('Adding received NEW_ICE candidate');
      await this.peerConnection.addIceCandidate(candidate);
    } catch (error) {
      console.error('Error adding NEW ICE candidate:', error);
      this.displayStatus(this.t('VIDEO.error.networkRoute'), 'error');
    }
  }

  private addIncomingMessageHandler(name: string): void {
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
    }

    console.log("Subscribing to incoming messages");
    this.messageSubscription = this.messages$.pipe(
      tap(msg => {
        if (msg.code == "101") {
          this.displayStatus(this.t('VIDEO.error.used'), 'error');
        } else {
          if (msg.data != null) {
            const msgType = msg.data.type;
            console.log(`Received message of type: ${msgType}`);

            switch (msgType) {
              case 'hangup':
              case 'hang-up':
                this.handleHangupMessage();
                break;
              case 'ice-candidate':
                this.handleICECandidateMessage(msg);
                break;
              case "video-offer":
                this.handleVideoOfferMsg(msg);
                break;
              case "video-answer":
                this.handleVideoAnswerMsg(msg);
                break;
              case "video-response":
                console.log("Received video response");
                // Only retry wake when signaling-server previously sent wake-up (camera was off).
                if (this.cameraRequiresWakeup) {
                  if (this.pendingPlaybackMap) {
                    this.retryPlaybackMapWakeCycle();
                  } else if (this.isCurrentlyWakingUp() && this.wakeupPurpose === 'playback-map') {
                    this.retryPlaybackMapWakeCycle();
                  }
                }
                this.displayStatus(this.t('VIDEO.status.acknowledged'), 'info');
                break;
              case "new-ice-candidate":
                this.handleNewICECandidateMsg(msg);
                break;
              case "wake-up":
              case "wakeup":
                this.handleWakeupMessage(msg.data);
                break;
              case "error":
                console.log("Error message:", JSON.stringify(msg.data));
                this.displayStatus(this.t('VIDEO.error.serverError') + ': ' + (msg.data.message || this.t('VIDEO.error.unknownError')), 'error');
                break;
              case "playback-map-response": {
                const mapPayload = msg.data as PlaybackMapResponsePayload;
                this.tryCompletePendingPlaybackMap(mapPayload);
                this.playbackMapResponseSubject$.next(mapPayload);
                break;
              }
              case "camera-config-response":
                console.log("Received camera-config-response");
                this.handleCameraConfigResponseReceived();
                break;
              default:
                console.log("Unhandled message type", msgType);
                break;
            }
          } else if (msg.event == "pong") {
            console.log(this.t('VIDEO.websocket.receivedPong'));
            this.pingPong = !this.pingPong;
          } else if (msg.event == "ping") {
            console.log(this.t('VIDEO.websocket.receivedPing'));
            this.pingPong = !this.pingPong;
            this.sendPongToServer(name);
          }
        }
      })
    ).subscribe(
      (msg: any) => {
        this.messages.push(msg);
      },
      (error) => {
        console.error('Error in message subscription:', error);
        this.displayStatus(this.t('VIDEO.error.processingMessages'), 'error');
        this.stopPing();
        this.clearWakeupRetry(); // NEW: This will also stop streaming wake-up
      }
    );
  }

  private handleWakeupMessage(data: any): void {
    console.log('Wake-up message received (camera is off, will retry):', data);
    const message = data?.message || this.t('VIDEO.wakeup.title') + '...';
    this.cameraRequiresWakeup = true;

    if (!this.isCurrentlyWakingUp()) {
      if (this.pendingPlaybackMap) {
        console.log('Starting playback-map wake-up with 15s retry interval');
        this.startWakeupRetryForPlaybackMap();
      } else if (this.lastCameraConfigCommand) {
        console.log('Starting camera-config wake-up with 15s retry interval');
        this.startWakeupRetryForCameraConfig();
      } else if (this.currentInfos) {
        console.log('Starting video wake-up with 15s retry interval');
        this.startWakeupRetry(this.currentCamera, this.currentInfos, this.currentIsPlayback);
      } else {
        console.warn('Wake-up message received but no current request data available');
        this.displayStatus(message, 'info');
      }
    } else {
      const remaining = this.getWakeupTimeRemaining();
      this.displayStatus(`${message} (${remaining}s remaining, retry every 15s)`, 'info');
    }
  }

  setUsername(name: string) {
    this.myUsername = name;
    // Register and ping are now sent from getNewWebSocket openObserver when socket is open
  }

  private startPing(name: string): void {
    this.stopPing();

    console.log(
      `${this.t('VIDEO.websocket.startingPing')}: pingv2 keepalive every ${this.PING_INTERVAL_MS}ms ` +
      `(first ping in ${this.PING_FIRST_DELAY_MS}ms)`
    );

    // Do not fire `event:ping` on the same tick as `register-client`/`pingv2`.
    // That race was closing the signaling socket right after connect on mobile.
    this.pingStartTimer = setTimeout(() => {
      this.pingStartTimer = null;
      if (!this.connexion || !this.socket$) {
        return;
      }
      console.log(this.t('VIDEO.websocket.sendingPing'));
      this.sendPingToServer(name);

      this.pingInterval = setInterval(() => {
        if (this.connexion && this.socket$) {
          console.log(this.t('VIDEO.websocket.sendingPing'));
          this.sendPingToServer(name);
        } else {
          console.warn(this.t('VIDEO.websocket.connectionLost'));
          this.stopPing();
        }
      }, this.PING_INTERVAL_MS);
    }, this.PING_FIRST_DELAY_MS);
  }

  public stopPing(): void {
    if (this.pingStartTimer) {
      clearTimeout(this.pingStartTimer);
      this.pingStartTimer = null;
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
      console.log(this.t('VIDEO.websocket.stoppedPing'));
    }
  }

  /** True when a camera stream/playback was started or is still negotiating. */
  private hasVideoSessionToHangUp(): boolean {
    return !!(
      this.stream ||
      this.playback ||
      this.videoRequestSent ||
      this.loadingStream ||
      this.isNegotiating
    );
  }

  /**
   * Tell the dashcam to end the current WebRTC session.
   * @param force Send on disconnect/destroy even while loadingStream / negotiating.
   */
  public sendHangUpMessage(vehicule: Vehicule | null | undefined, force = false): void {
    const targetId = vehicule?.device?.streamId;
    if (!targetId || !this.socket$ || !this.connexion || !this.myUsername) {
      return;
    }
    if (!force && this.loadingStream) {
      console.log('Skipping hang-up: ICE negotiation in progress');
      return;
    }
    if (!force && !this.hasVideoSessionToHangUp()) {
      return;
    }
    try {
      console.log('Sending hang-up to camera:', targetId);
      this.sendMessage({
        event: 'message',
        data: {
          source: this.myUsername,
          target: targetId,
          type: 'hang-up'
        }
      });
    } catch (error) {
      console.warn('Error sending hang-up message:', error);
    }
  }

  disconnectSocket(vehicule: Vehicule) {
    console.log(this.t('VIDEO.cleanup.disconnecting'));

    try {
      this.stopPing();
      if (this.pendingPlaybackMap) {
        this.cancelPendingPlaybackMap(new Error('Signaling disconnected'));
      }
      this.clearWakeupRetry();

      this.sendHangUpMessage(vehicule, true);
      this.closeVideoCall();

      // Safe video cleanup without destroying element
      this.safeVideoElementReset();

      this.sendMessage({
        event: "message",
        data: {
          source: this.myUsername,
          target: vehicule.device.streamId || "server",
          type: "disconnect"
        }
      });

      if (this.messageSubscription) {
        this.messageSubscription.unsubscribe();
        this.messageSubscription = null;
      }

      if (this.socketSubscription) {
        this.socketSubscription.unsubscribe();
        this.socketSubscription = null;
      }

      if (this.socket$) {
        const socket = this.socket$;
        this.socket$ = null;
        try {
          socket.complete();
        } catch {
          // ignore
        }
      }

      this.connexion = false;
      this.socketLoading = false;

    } catch (error) {
      console.error(this.t('VIDEO.cleanup.errorDisconnecting'), error);
      this.socketSubscription = null;
      this.socket$ = null;
      this.connexion = false;
      this.socketLoading = false;
      this.stopPing();
      this.clearWakeupRetry();
    } finally {
      // Always clear transient UI/connection flags so camera buttons are usable on next connect.
      this.resetVideoSessionFlags();
      this.camChuaffeur = false;
      this.camFrontFacing = false;
      this.camRearFacing = false;
    }
  }

  /**
   * Full camera session teardown for disconnect buttons and component destroy.
   * Sends hang-up (including during negotiation), closes WebRTC, and disconnects signaling.
   */
  public teardownVideoSession(vehicule?: Vehicule | null): void {
    const v = vehicule ?? this.vehicule ?? null;
    this.showVideoModal = false;

    if (!v?.device?.streamId) {
      this.stopPing();
      this.clearWakeupRetry();
      this.closeVideoCall();
      this.resetVideoSessionFlags();
      return;
    }

    if (this.connexion && this.socket$) {
      this.disconnectSocket(v);
      return;
    }

    this.stopPing();
    this.clearWakeupRetry();
    this.sendHangUpMessage(v, true);
    this.closeVideoCall();
    this.safeVideoElementReset();
    this.resetVideoSessionFlags();
    this.camChuaffeur = false;
    this.camFrontFacing = false;
    this.camRearFacing = false;
  }

  public hangUpCall(vehicule: Vehicule) {
    try {
      console.log('Hanging up call with:', vehicule);
      this.sendHangUpMessage(vehicule, false);
      this.closeVideoCall();
      this.displayStatus(this.t('VIDEO.status.callEnded'), 'info');
      this.countBackToConnect();
    } catch (error) {
      console.error(this.t('VIDEO.error.endingCall'), error);
      this.displayStatus(this.t('VIDEO.error.endingCall'), 'error');
      this.closeVideoCall();
    }
  }

  streamOrPlayback(param: string, paramPlayback: string) {
    if (this.type === "stream") {
      this.Steam(param);
    } else {
      const date = this.playbackSeekToken
        || (this.path?.displayBeginPathTime
          ? formatPlaybackSeekToken(new Date(this.path.displayBeginPathTime))
          : '');
      if (!date) {
        this.displayStatus(this.t('VIDEO.playback.failed'), 'error');
        return;
      }
      this.Playback(date, paramPlayback);
    }
  }

  /** Prefer UTC seek token (fleet-12); fall back to path begin time as GMT. */
  formatPathPlaybackDate(path: Path): string {
    if (!path?.displayBeginPathTime) {
      return '';
    }
    return formatPlaybackSeekToken(new Date(path.displayBeginPathTime));
  }

  setPlaybackRange(startMs: number, endMs?: number, range?: PlaybackMapRange | null): void {
    const segmentStart = range
      ? parsePlaybackMapTokenToMillis(range.from)
      : startMs;
    const segmentEnd = range
      ? parsePlaybackMapTokenToMillis(range.to)
      : endMs;
    const alignedMs = alignSeekToPlaybackChunkStart(
      startMs,
      segmentStart,
      segmentEnd
    );

    this.recordingRange = range || null;
    this.playbackSeekToken = formatPlaybackSeekToken(new Date(alignedMs));
    if (!this.path) {
      this.path = {} as Path;
    }
    this.path.displayBeginPathTime = new Date(alignedMs);
    if (segmentEnd != null && Number.isFinite(segmentEnd)) {
      this.path.displayEndPathTime = new Date(segmentEnd);
    } else if (endMs != null) {
      this.path.displayEndPathTime = new Date(endMs);
    }
  }

  clearPlaybackContext(): void {
    this.playbackSeekToken = null;
    this.recordingRange = null;
    this.playbackMapRanges = [];
    this.path = null;
    this.type = '';
    this.returnRoute = '/client';
  }

  /** True when a historical video-request was already sent for the current segment. */
  public isHistoricalPlaybackPendingOrActive(): boolean {
    return this.type === 'playback' && (
      this.playback ||
      this.loadingStream ||
      this.videoRequestSent ||
      this.isNegotiating
    );
  }

  /**
   * Fleet-12 equivalent of TrackControle.selectCamera → VideoService.Playback:
   * ensure signaling, then send `video-request` with `PNormal/CHx/CHxREC…`.
   */
  async startHistoricalPlayback(defaultChannel = 'CH1'): Promise<void> {
    if (!this.vehicule) {
      this.vehicule = this.dataManagementService.selectedVehicule;
    }
    if (!this.vehicule) {
      console.warn('startHistoricalPlayback: no vehicle');
      this.displayStatus(this.t('VIDEO.error.noVehicle'), 'warning');
      return;
    }

    this.type = 'playback';

    try {
      await this.ensureSignalingForVehicle(this.vehicule);
    } catch (error) {
      console.error('startHistoricalPlayback: signaling failed', error);
      this.displayStatus(this.t('VIDEO.connection.socketTimeout'), 'error');
      return;
    }

    const date = this.playbackSeekToken || (this.path ? this.formatPathPlaybackDate(this.path) : null);
    if (!date) {
      console.warn('startHistoricalPlayback: missing seek token');
      this.displayStatus(this.t('VIDEO.playback.failed'), 'error');
      return;
    }

    // Avoid duplicate video-request while one is already in flight for this session.
    if (this.isHistoricalPlaybackPendingOrActive() && this.currentInfos?.includes(`REC${date}`)) {
      console.log('startHistoricalPlayback: request already in flight for', date);
      return;
    }

    await this.Playback(date, defaultChannel);
  }

  countBackToConnect(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.counterLoading = true;
    this.counter = 30;
    this.displayStatus(this.t('VIDEO.connecting'), 'info');
    this.countdownInterval = setInterval(() => {
      this.counter--;
      if (this.counter <= 0) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
        this.counterLoading = false;
        this.displayStatus(this.t('VIDEO.connection.established'), 'success');
      }
    }, 1000);
  }

  public resetService(): void {
    this.stopPing();
    this.clearIceDisconnectTimer();
    this.stopCandidateStatsLogging();
    this.clearWakeupRetry();

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    this.counter = 0;
    this.playbackSeekToken = null;
    this.recordingRange = null;
    this.playbackMapRanges = [];

    if (this.vehicule) {
      this.sendHangUpMessage(this.vehicule, true);
    }

    // Safe video cleanup
    this.safeVideoElementReset();
    this.videoElementState.isReady = false;
    this.videoElementState.elementRef = null;
    this.videoElementState.bufferedEvents = [];

    this.path = null;
    this.remoteVideo = null;

    if (this.socket$) {
      this.socket$.complete();
      this.socket$ = null;
    }

    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
      this.messageSubscription = null;
    }

    this.messagesSubject$ = new Subject<any>();
    this.messages$ = this.messagesSubject$.asObservable();
    this.messages = [];
    this.myUsername = "";
    this.pingPong = false;
    this.resetVideoSessionFlags();
    this.socketLoading = false;
    this.counterLoading = false;
    this.connexion = false;
    this.camChuaffeur = false;
    this.camFrontFacing = false;
    this.camRearFacing = false;

    if (this.peerConnection) {
      this.peerConnection.close();
    }
    this.createPeerConnection();

    this.vehicule = this.dataManagementService.selectedVehicule;
  }

  /**
   * True once a video-request has established (or is establishing) a session.
   * Further channel/time updates must use switch-cam, not a new video-request.
   */
  private hasActiveVideoSession(): boolean {
    if (this.stream || this.playback) {
      return true;
    }
    // Playback negotiating after the initial video-request (not on the same call that sends it)
    return !!(
      this.videoRequestSent &&
      this.loadingStream &&
      this.currentIsPlayback &&
      this.currentInfos
    );
  }

  private markVideoRequestSent(): void {
    this.videoRequestSent = true;
  }

  private resetVideoSessionFlags(): void {
    this.videoRequestSent = false;
    this.stream = false;
    this.playback = false;
    this.loadingStream = false;
    this.isNegotiating = false;
  }

  private sendSwitchCam(infos: string): void {
    if (!this.vehicule?.device?.streamId) {
      return;
    }
    console.log('Sending switch-cam:', infos);
    this.sendMessage({
      event: 'message',
      data: {
        source: this.myUsername,
        type: 'switch-cam',
        infos,
        target: this.vehicule.device.streamId
      }
    });
    this.playbackSourceSwitchSubject$.next(infos);
  }

  async Steam(param: string): Promise<void> {
    const sessionActive = this.hasActiveVideoSession();
    // Do not send new video-request while ICE negotiation in progress (would interrupt dashcam)
    if (this.isNegotiating && !sessionActive) {
      console.log('Skipping video-request: ICE negotiation already in progress');
      return;
    }

    switch (param) {
      case "S5":
        this.camChuaffeur = true;
        this.camFrontFacing = false;
        this.camRearFacing = false;
        this.displayStatus(this.t('VIDEO.camera.switchingToDriver'), 'info');
        break;
      case "S4":
        this.camChuaffeur = false;
        this.camFrontFacing = true;
        this.camRearFacing = false;
        this.displayStatus(this.t('VIDEO.camera.switchingToFront'), 'info');
        break;
      default:
        this.camChuaffeur = false;
        this.camFrontFacing = false;
        this.camRearFacing = true;
        this.displayStatus(this.t('VIDEO.camera.switchingToRear'), 'info');
        break;
    }

    // Only set loading when initial request (negotiation); switch-cam reuses the same session
    if (!sessionActive) {
      this.loadingStream = true;
    }
    console.log("Selected vehicle for streaming:", this.vehicule);

    try {
      // Store current request info; wake retries only if signaling-server sends wake-up
      this.resetCameraWakeupExpectation();
      this.currentCamera = param;
      this.currentInfos = param;
      this.currentIsPlayback = false;

      if (!sessionActive) {
        this.displayStatus(this.t('VIDEO.stream.requesting'), 'info');
        this.markVideoRequestSent();
        this.sendMessage({
          event: 'message',
          data: {
            source: this.myUsername,
            type: 'video-request',
            infos: param,
            target: this.vehicule.device.streamId
          }
        });
      } else {
        this.displayStatus(this.t('VIDEO.camera.switching'), 'info');
        this.sendSwitchCam(param);
      }

      // Do NOT create offer here: server sends video-offer, we answer in handleVideoOfferMsg (same as cam_simulation)
      // this.stream and loadingStream are set when track is received (handleTrackEvent / onLoadedData)
    } catch (error) {
      console.error("Could not send video request:", error);
      this.loadingStream = false;
      this.displayStatus(this.t('VIDEO.stream.failed'), 'error');
    }
  }

  async Playback(date: string, param: string): Promise<void> {
    const sessionActive = this.hasActiveVideoSession();
    // Do not send new video-request while ICE negotiation in progress (would interrupt dashcam)
    if (this.isNegotiating && !sessionActive) {
      console.log('Skipping video-request: ICE negotiation already in progress');
      return;
    }

    switch (param) {
      case "CH2":
        this.camChuaffeur = true;
        this.camFrontFacing = false;
        this.camRearFacing = false;
        this.displayStatus(this.t('VIDEO.playback.loadingDriver'), 'info');
        break;
      case "CH1":
        this.camChuaffeur = false;
        this.camFrontFacing = true;
        this.camRearFacing = false;
        this.displayStatus(this.t('VIDEO.playback.loadingFront'), 'info');
        break;
      default:
        this.camChuaffeur = false;
        this.camFrontFacing = false;
        this.camRearFacing = true;
        this.displayStatus(this.t('VIDEO.playback.loadingRear'), 'info');
        break;
    }

    if (!sessionActive) {
      this.loadingStream = true;
    }
    console.log("Selected vehicle for playback:", this.vehicule);

    const infos = `PNormal/${param}/${param}REC${date}`;

    try {
      // Store current request info; wake retries only if signaling-server sends wake-up
      this.resetCameraWakeupExpectation();
      this.currentCamera = param;
      this.currentInfos = infos;
      this.currentIsPlayback = true;

      this.displayStatus(
        this.t(sessionActive ? 'VIDEO.playback.switching' : 'VIDEO.playback.requesting'),
        'info'
      );
      this.sendPlaybackRequest(infos);

      // Do NOT create offer here: server sends video-offer, we answer in handleVideoOfferMsg (same as cam_simulation)
      // this.playback and loadingStream are set when track is received (onLoadedData)
    } catch (error) {
      console.error("Could not send playback request:", error);
      this.loadingStream = false;
      this.displayStatus(this.t('VIDEO.playback.failed'), 'error');
    }
  }

  /**
   * Update historical playback source (channel / time offset).
   * Uses switch-cam when a video session is already active; video-request only for the first connect.
   */
  public sendPlaybackRequest(infos: string): void {
    this.currentInfos = infos;
    this.currentIsPlayback = true;
    this.wakeupSavedInfos = infos;

    if (this.hasActiveVideoSession()) {
      this.sendSwitchCam(infos);
      return;
    }

    this.markVideoRequestSent();
    const payload = {
      event: 'message',
      data: {
        source: this.myUsername,
        type: 'video-request',
        infos,
        target: this.vehicule?.device?.streamId
      }
    };
    console.log('Playback video-request payload:', JSON.stringify(payload));
    this.sendMessage(payload);
  }

  /**
   * Wait until the signaling WebSocket is registered and ready (after connect / openObserver).
   */
  waitForWebSocketConnected(timeoutMs: number): Promise<void> {
    if (this.connexion) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const t0 = Date.now();
      const id = setInterval(() => {
        if (this.connexion) {
          clearInterval(id);
          resolve();
        } else if (Date.now() - t0 > timeoutMs) {
          clearInterval(id);
          reject(new Error('WebSocket connection timeout'));
        }
      }, 100);
    });
  }

  /**
   * Open or reuse signaling for the given vehicle. Disconnects first if another device was connected.
   */
  async ensureSignalingForVehicle(vehicule: Vehicule): Promise<void> {
    if (!vehicule?.device?.streamId) {
      throw new Error('Vehicle has no camera stream id');
    }
    if (this.connexion && this.vehicule?.device?.streamId !== vehicule.device.streamId) {
      this.disconnectSocket(this.vehicule);
    }
    this.vehicule = vehicule;
    if (this.connexion) {
      return;
    }
    // Another caller may already be connecting (segment open + camera page race).
    if (this.socketLoading && this.socket$) {
      await this.waitForWebSocketConnected(25000);
      return;
    }
    this.onConnectClick(vehicule);
    await this.waitForWebSocketConnected(25000);
  }

  /**
   * Ask the dashcam for recording ranges between two tokens `YYYYMMDD-HHmmss` (inclusive window).
   * Requires an active signaling connection for the same vehicle (`ensureSignalingForVehicle`).
   */
  requestPlaybackMap(start: string, end: string): Promise<PlaybackMapResponsePayload> {
    if (this.pendingPlaybackMap) {
      return Promise.reject(new Error('playback-map request already in progress'));
    }
    if (!this.socket$ || !this.connexion || !this.myUsername) {
      return Promise.reject(new Error('Signaling not connected'));
    }
    if (!this.vehicule?.device?.streamId) {
      return Promise.reject(new Error('No vehicle stream id'));
    }

    return new Promise((resolve, reject) => {
      const timeoutTimer = setTimeout(() => {
        this.cancelPendingPlaybackMap(new Error('playback-map response timeout'));
      }, PLAYBACK_MAP_RESPONSE_TIMEOUT_MS);

      const responseSub = this.playbackMapResponse$.subscribe((data: PlaybackMapResponsePayload) => {
        this.tryCompletePendingPlaybackMap(data);
      });

      this.pendingPlaybackMap = {
        start,
        end,
        resolve,
        reject,
        timeoutTimer,
        responseSub
      };

      this.preparePlaybackMapWakeupSession();
      this.sendPlaybackMapCommand(start, end);
    });
  }

  onConnectClick(vehicule: Vehicule): void {
    if (this.connexion) {
      this.displayStatus(this.t('VIDEO.alreadyConnected'), 'info');
      return;
    }
    if (this.socketLoading && this.socket$) {
      console.warn('onConnectClick: connection already in progress');
      this.displayStatus(this.t('VIDEO.connecting'), 'info');
      return;
    }

    this.displayStatus(this.t('VIDEO.connecting'), 'info');
    this.vehicule = vehicule;
    const browserId = this.getBrowserId();
    this.myUsername = browserId;
    this.addIncomingMessageHandler(browserId);
    const connection = this.connect();
    if (connection) {
      console.log('Connecting to vehicle:', vehicule);
    }
  }

  sendPongToServer(name: string) {
    this.sendMessage({
      event: "pong",
      data: {name: name}
    });
  }

  sendPingToServer(name: string) {
    this.sendMessage({
      event: "ping",
      data: {name: name}
    });
  }

  async handleNegotiationNeededEvent() {
    // Client never creates offer: server (dashcam) sends video-offer, we answer in handleVideoOfferMsg (same as cam_simulation)
    console.log('Negotiation needed (client waits for server offer, no local offer)');
  }

  private handleDataChannel = () => {
    console.log("Data channel event received");
  }

  private handleSignalingStateChangeEvent = () => {
    console.log("WebRTC signaling state changed to:", this.peerConnection.signalingState);

    if (this.peerConnection.signalingState === 'closed') {
      console.log("Connection closed");
      this.displayStatus(this.t('VIDEO.connection.closed'), 'info');
    }
  }

  private handleICEGatheringStateChangeEvent = () => {
    console.log("ICE gathering state changed to:", this.peerConnection.iceGatheringState);
  }

  private handleICEConnectionStateChangeEvent = () => {
    console.log("ICE connection state changed to:", this.peerConnection.iceConnectionState);

    switch (this.peerConnection.iceConnectionState) {
      case 'closed':
        this.clearIceDisconnectTimer();
        console.log("ICE connection closed");
        this.displayStatus(this.t('VIDEO.connection.closed'), 'info');
        break;
      case 'failed':
        this.clearIceDisconnectTimer();
        console.log("ICE connection failed");
        this.loadingStream = false;
        this.isNegotiating = false;
        this.displayStatus(this.t('VIDEO.connection.error'), 'error');
        break;
      case 'disconnected':
        console.log("ICE connection disconnected");
        this.scheduleIceDisconnectCheck();
        break;
      case 'connected':
      case 'completed':
        this.clearIceDisconnectTimer();
        this.startCandidateStatsLogging();
        // ICE complete ≠ frames painted. Re-bind receivers and retry play
        // (fixes Capacitor black screen when track fired early / muted).
        this.recoverRemoteVideoPlayback(`ice-${this.peerConnection.iceConnectionState}`);
        this.displayStatus(this.t('VIDEO.connection.networkEstablished'), 'success');
        break;
    }
  }

  private handleConnectionStateChangeEvent = () => {
    if (!this.peerConnection) {
      return;
    }
    console.log('WebRTC connection state changed to:', this.peerConnection.connectionState);
    if (this.peerConnection.connectionState === 'connected') {
      this.recoverRemoteVideoPlayback('pc-connected');
    }
  };

  private scheduleIceDisconnectCheck(): void {
    if (this.iceDisconnectTimer) {
      return;
    }
    this.iceDisconnectTimer = setTimeout(() => {
      this.iceDisconnectTimer = null;
      if (!this.peerConnection) {
        return;
      }
      if (this.peerConnection.iceConnectionState === 'disconnected') {
        console.log("ICE remained disconnected after grace period, closing call");
        this.closeVideoCall();
        this.displayStatus(this.t('VIDEO.disconnected'), 'warning');
      }
    }, this.ICE_DISCONNECT_GRACE_MS);
  }

  private clearIceDisconnectTimer(): void {
    if (this.iceDisconnectTimer) {
      clearTimeout(this.iceDisconnectTimer);
      this.iceDisconnectTimer = null;
    }
  }

  private startCandidateStatsLogging(): void {
    if (this.candidateStatsInterval) {
      return;
    }
    this.candidateStatsInterval = setInterval(() => {
      this.logSelectedCandidatePairStats();
    }, this.CANDIDATE_STATS_INTERVAL_MS);
    this.logSelectedCandidatePairStats();
  }

  private stopCandidateStatsLogging(): void {
    if (this.candidateStatsInterval) {
      clearInterval(this.candidateStatsInterval);
      this.candidateStatsInterval = null;
    }
    this.lastLoggedCandidatePairId = null;
  }

  private async logSelectedCandidatePairStats(): Promise<void> {
    if (!this.peerConnection || this.peerConnection.connectionState === 'closed') {
      return;
    }
    try {
      const stats = await this.peerConnection.getStats();
      let selectedPair: any = null;
      let localCandidate: any = null;
      let remoteCandidate: any = null;
      const statsById: { [id: string]: any } = {};

      stats.forEach((report: any) => {
        if (report?.id) {
          statsById[report.id] = report;
        }
        if (report.type === 'candidate-pair' && report.selected) {
          selectedPair = report;
        }
      });

      if (!selectedPair) {
        stats.forEach((report: any) => {
          if (report.type === 'transport' && report.selectedCandidatePairId) {
            selectedPair = statsById[report.selectedCandidatePairId];
          }
        });
      }

      if (!selectedPair) {
        return;
      }

      if (selectedPair.localCandidateId) {
        localCandidate = statsById[selectedPair.localCandidateId];
      }
      if (selectedPair.remoteCandidateId) {
        remoteCandidate = statsById[selectedPair.remoteCandidateId];
      }

      const summary = {
        pairId: selectedPair.id,
        state: selectedPair.state,
        nominated: selectedPair.nominated,
        pairPriority: selectedPair.priority,
        currentRttMs: selectedPair.currentRoundTripTime != null ? Math.round(selectedPair.currentRoundTripTime * 1000) : null,
        totalRttMs: selectedPair.totalRoundTripTime != null ? Math.round(selectedPair.totalRoundTripTime * 1000) : null,
        bytesSent: selectedPair.bytesSent,
        bytesReceived: selectedPair.bytesReceived,
        local: localCandidate ? {
          candidateType: localCandidate.candidateType,
          protocol: localCandidate.protocol,
          relayProtocol: localCandidate.relayProtocol,
          networkType: localCandidate.networkType,
          address: localCandidate.address || localCandidate.ip,
          port: localCandidate.port,
          priority: localCandidate.priority
        } : null,
        remote: remoteCandidate ? {
          candidateType: remoteCandidate.candidateType,
          protocol: remoteCandidate.protocol,
          relayProtocol: remoteCandidate.relayProtocol,
          address: remoteCandidate.address || remoteCandidate.ip,
          port: remoteCandidate.port,
          priority: remoteCandidate.priority
        } : null
      };

      if (this.lastLoggedCandidatePairId !== selectedPair.id) {
        console.log('[WebRTC] Selected ICE candidate pair changed:', summary);
        this.lastLoggedCandidatePairId = selectedPair.id;
      } else {
        console.log('[WebRTC] Selected ICE candidate pair stats:', {
          pairId: summary.pairId,
          state: summary.state,
          currentRttMs: summary.currentRttMs,
          bytesSent: summary.bytesSent,
          bytesReceived: summary.bytesReceived,
          pairPriority: summary.pairPriority,
          localType: summary.local?.candidateType,
          remoteType: summary.remote?.candidateType
        });
      }
    } catch (error) {
      console.warn('[WebRTC] Failed to read candidate stats:', error);
    }
  }

  private handleICECandidateEvent = (event: RTCPeerConnectionIceEvent) => {
    if (event.candidate) {
      console.log("Outgoing ICE candidate");

      this.sendMessage({
        event: "message",
        data: {
          candidate: event.candidate,
          source: this.myUsername,
          target: this.vehicule.device.streamId,
          type: 'new-ice-candidate',
        }
      });
    }
  }

  public createPeerConnection(): void {
    this.stopCandidateStatsLogging();
    this.peerConnection = new RTCPeerConnection(peerConnectionConfig);

    this.peerConnection.onicecandidate = this.handleICECandidateEvent;
    this.peerConnection.oniceconnectionstatechange = this.handleICEConnectionStateChangeEvent;
    this.peerConnection.onconnectionstatechange = this.handleConnectionStateChangeEvent;
    this.peerConnection.onicegatheringstatechange = this.handleICEGatheringStateChangeEvent;
    this.peerConnection.onsignalingstatechange = this.handleSignalingStateChangeEvent;
    this.peerConnection.ondatachannel = this.handleDataChannel;
    this.peerConnection.ontrack = this.handleTrackEvent;
    this.peerConnection.onnegotiationneeded = this.handleNegotiationNeededEvent.bind(this);

    console.log("Peer connection created with config:", peerConnectionConfig);
  }

  public closeVideoCall(): void {
    console.log(this.t('VIDEO.cleanup.closingCall'));

    try {
      this.clearIceDisconnectTimer();
      this.stopCandidateStatsLogging();
      if (!this.peerConnection) {
        console.log(this.t('VIDEO.cleanup.noPeerConnection'));
        return;
      }

      if (this.peerConnection.connectionState === 'closed' ||
        this.peerConnection.signalingState === 'closed') {
        console.log(this.t('VIDEO.cleanup.alreadyClosed'));
        this.peerConnection = null;
        this.resetStreamState();
        this.createPeerConnection();
        return;
      }

      this.peerConnection.ontrack = null;
      this.peerConnection.onicecandidate = null;
      this.peerConnection.oniceconnectionstatechange = null;
      this.peerConnection.onconnectionstatechange = null;
      this.peerConnection.onsignalingstatechange = null;
      this.peerConnection.onnegotiationneeded = null;
      this.peerConnection.ondatachannel = null;
      this.peerConnection.onicegatheringstatechange = null;

      try {
        const transceivers = this.peerConnection.getTransceivers();
        transceivers.forEach((transceiver, index) => {
          try {
            if (transceiver.receiver?.track) {
              transceiver.receiver.track.stop();
            }
            if (transceiver.sender?.track) {
              transceiver.sender.track.stop();
            }
            if (this.peerConnection.connectionState !== 'closed') {
              transceiver.stop();
            }
          } catch (error) {
            console.warn(`Error stopping transceiver ${index}:`, error);
          }
        });
      } catch (error) {
        console.warn('Error accessing transceivers:', error);
      }

      try {
        this.peerConnection.close();
      } catch (error) {
        console.warn('Error closing peer connection:', error);
      }

      this.peerConnection = null;

    } catch (error) {
      console.error(this.t('VIDEO.cleanup.errorClosing'), error);
      this.displayStatus(this.t('VIDEO.cleanup.errorClosing'), 'error');
      this.peerConnection = null;
    } finally {
      this.resetStreamState();
      this.createPeerConnection();
    }
  }

  private resetStreamState(): void {
    this.resetVideoSessionFlags();
    this.camChuaffeur = false;
    this.camRearFacing = false;
    this.camFrontFacing = false;
  }

  public checkVideoElementStatus(): void {
    console.log('=== Video Element Status ===');
    console.log('Video element ready:', this.videoElementState.isReady);
    console.log('Element ref exists:', !!this.videoElementState.elementRef);
    console.log('Buffered events:', this.videoElementState.bufferedEvents.length);
    console.log('Camera requires wake-up:', this.cameraRequiresWakeup);
    console.log('=== End Status ===');
  }
}
